import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminShell from "./AdminShell";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { v: "nuovo", label: "Nuovo", color: "bg-sky-100 text-sky-800 border-sky-200" },
  { v: "in_lavorazione", label: "In lavorazione", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { v: "completato", label: "Completato", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { v: "annullato", label: "Annullato", color: "bg-slate-100 text-slate-600 border-slate-200" },
];

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find((s) => s.v === status) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${opt.color}`}>
      {opt.label}
    </span>
  );
}

export default function ApeRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ape-requests", { params: filter !== "all" ? { status: filter } : {} });
      setItems(data.items || []);
    } catch {
      toast.error("Impossibile caricare le richieste");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function updateStatus(id, status) {
    try {
      await api.patch(`/admin/ape-requests/${id}`, { status });
      toast.success("Stato aggiornato");
      load();
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    } catch { toast.error("Errore"); }
  }

  async function saveNotes() {
    if (!selected) return;
    try {
      await api.patch(`/admin/ape-requests/${selected.id}`, { admin_notes: notes });
      toast.success("Note salvate");
      load();
    } catch { toast.error("Errore"); }
  }

  async function del(id) {
    if (!window.confirm("Eliminare definitivamente questa richiesta?")) return;
    try {
      await api.delete(`/admin/ape-requests/${id}`);
      toast.success("Richiesta eliminata");
      if (selected?.id === id) setSelected(null);
      load();
    } catch { toast.error("Errore"); }
  }

  const counts = {
    all: items.length,
    nuovo: items.filter((i) => (i.status || "nuovo") === "nuovo").length,
  };

  return (
    <AdminShell title="Servizio APE — Richieste">
      <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="ape-filter-bar">
        {[{ v: "all", label: "Tutte" }, ...STATUS_OPTIONS].map((s) => (
          <button
            key={s.v}
            type="button"
            onClick={() => setFilter(s.v)}
            className={`text-xs font-medium px-3 py-1.5 rounded-md border ${
              filter === s.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
            }`}
            data-testid={`ape-filter-${s.v}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ape-list">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Nessuna richiesta.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Data</th>
                  <th className="text-left px-4 py-2">Nome</th>
                  <th className="text-left px-4 py-2">Contatti</th>
                  <th className="text-left px-4 py-2">Stato</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const isActive = selected?.id === it.id;
                  return (
                    <tr
                      key={it.id}
                      onClick={() => { setSelected(it); setNotes(it.admin_notes || ""); }}
                      className={`border-t border-slate-100 cursor-pointer ${isActive ? "bg-slate-50" : "hover:bg-slate-50"}`}
                      data-testid={`ape-row-${it.id}`}
                    >
                      <td className="px-4 py-3 text-xs text-slate-600 tabular-nums whitespace-nowrap">
                        {new Date(it.created_at).toLocaleDateString("it-IT")}<br/>
                        <span className="text-slate-400">{new Date(it.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">{it.nome}</td>
                      <td className="px-4 py-3 text-xs">
                        <div className="text-slate-700">{it.email}</div>
                        <div className="text-slate-500">{it.telefono}</div>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={it.status || "nuovo"} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="ape-detail">
          {selected ? (
            <>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-serif text-lg text-slate-900">{selected.nome}</h3>
                  <p className="text-xs text-slate-500">Ricevuta il {new Date(selected.created_at).toLocaleString("it-IT")}</p>
                </div>
                <button type="button" onClick={() => del(selected.id)} className="text-xs text-red-700 hover:underline" data-testid="ape-detail-delete">Elimina</button>
              </div>
              <dl className="space-y-2 text-sm">
                <div><dt className="text-xs uppercase tracking-wide text-slate-400">Email</dt><dd><a className="text-slate-900 hover:underline" href={`mailto:${selected.email}`}>{selected.email}</a></dd></div>
                <div><dt className="text-xs uppercase tracking-wide text-slate-400">Telefono</dt><dd><a className="text-slate-900 hover:underline" href={`tel:${selected.telefono}`}>{selected.telefono}</a></dd></div>
                <div><dt className="text-xs uppercase tracking-wide text-slate-400">Indirizzo immobile</dt><dd className="text-slate-800">{selected.indirizzo_immobile || "—"}</dd></div>
                <div><dt className="text-xs uppercase tracking-wide text-slate-400">Codice fiscale / n. associato</dt><dd className="text-slate-800">{selected.codice_fiscale || "—"}</dd></div>
                <div><dt className="text-xs uppercase tracking-wide text-slate-400">Note utente</dt><dd className="text-slate-800 whitespace-pre-wrap">{selected.note || "—"}</dd></div>
                <div><dt className="text-xs uppercase tracking-wide text-slate-400">Submission ID</dt><dd className="text-slate-500 text-xs font-mono break-all">{selected.submission_id || "—"}</dd></div>
              </dl>

              <div className="mt-5">
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Cambia stato</div>
                <div className="flex gap-2 flex-wrap">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.v}
                      type="button"
                      onClick={() => updateStatus(selected.id, s.v)}
                      className={`text-[11px] font-medium px-2.5 py-1 rounded border ${(selected.status || "nuovo") === s.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                      data-testid={`ape-status-${s.v}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <label className="text-xs uppercase tracking-wide text-slate-400">Note interne admin</label>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full mt-1.5 border border-slate-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Es. contattato il 12/03, appuntamento fissato..."
                  data-testid="ape-admin-notes"
                />
                <button
                  type="button"
                  onClick={saveNotes}
                  className="mt-2 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-medium"
                  data-testid="ape-save-notes"
                >
                  Salva note
                </button>
              </div>
            </>
          ) : (
            <div className="py-12 text-center text-sm text-slate-500">Seleziona una richiesta dalla lista.</div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
